import { type KeyboardEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Switch } from '@thanx/uikit/switch'
import SegmentActivationModal from 'components/AISegmentBuilder/SegmentActivationModal'
import ThanxAISegmentNameModal from 'components/AISegmentBuilder/SegmentNameModal'
import { useAISegmentBuilderActions } from 'hooks/useAISegmentBuilderActions'
import useFlag from 'hooks/useFlag'
import { useSegmentActivationPreference } from 'hooks/useSegmentActivationPreference'
import type { SegmentVisualizationData } from 'slices/aiConversations/segmentVisualizationTypes'
import {
  activationCancelled,
  activationOptionSelected,
  activationStarted,
  flowReset,
  flowStarted,
  nameEdited,
  openSegmentDetailsModal,
  rememberChoiceToggled,
  selectActivationFlow,
  selectIsSegmentSaving,
} from 'slices/aiSegmentBuilder'
import {
  activationSaveClickedThunk,
  segmentNameSubmittedThunk,
} from 'slices/aiSegmentBuilder/thunks'

import SegmentActions from 'components/AISegmentBuilder/SegmentVisualizationActions/SegmentActions'
import SegmentCampaignActions from 'components/AISegmentBuilder/SegmentVisualizationActions/SegmentCampaignActions'
import SegmentFiltersHeader from './SegmentFiltersHeader'
import SegmentGroupsList from './SegmentGroupsList'
import SegmentRawDsl from './SegmentRawDsl'

import './ThanxAISegmentVisualization.scss'

export type {
  GroupOperator,
  RuleType,
  SegmentRule,
  SegmentGroup,
  SegmentVisualizationData,
} from 'slices/aiConversations/segmentVisualizationTypes'

export interface ThanxAISegmentVisualizationProps {
  data: SegmentVisualizationData
  instanceId: string
  isLatestDraftSegment?: boolean
  isLatestSavedSegment?: boolean
  hasAnySavedSegment?: boolean
}

const ThanxAISegmentVisualization = ({
  data,
  instanceId,
  isLatestDraftSegment = false,
  isLatestSavedSegment = false,
  hasAnySavedSegment = false,
}: ThanxAISegmentVisualizationProps) => {
  const dispatch = useDispatch()
  const aiSegmentDetailsEnabled = useFlag('ai-segment-details-enabled', false)
  const aiSegmentDevtoolsEnabled = useFlag('ai-segment-devtools-enabled', false)
  const autoApproveEnabled = useFlag('mx-segment-auto-approve', false)

  const activationFlow = useSelector(selectActivationFlow)
  const isSegmentSaving = useSelector(selectIsSegmentSaving)
  const { preference, update: updatePreference } =
    useSegmentActivationPreference()

  const { onCampaignCreate, isCampaignLoading } = useAISegmentBuilderActions()

  const groups = data.content.filters.groups
  const hasRawDsl = !!data.rawDsl

  const isOwnFlow = activationFlow.instanceId === instanceId
  const isNameModalOpen =
    isOwnFlow && activationFlow.phase === 'naming' && !isSegmentSaving

  // pendingAutoApprove takes precedence over server preference — mirrors thunk logic.
  // When set it reflects the user's current session choice even if not yet saved.
  const effectiveAutoApprove =
    activationFlow.pendingAutoApprove != null
      ? activationFlow.pendingAutoApprove === 'activate'
      : preference?.autoApproveAiSegments ?? null

  const buildSegmentData = (name: string, autoApprove?: boolean) => ({
    title: data.title,
    description: data.description,
    filters: data.content.filters,
    rawDsl: data.rawDsl,
    estimatedCount: data.estimatedCount,
    hasComplexStructure: data.hasComplexStructure,
    autoApprove,
  })

  // Named handlers (no-anonymous-props)
  const handleViewCustomers = () => {
    // TODO: Implement view customers functionality
  }

  const handleSaveClick = () => {
    dispatch(flowStarted({ initialName: data.title, instanceId }))
  }

  const handleNameModalClose = () => {
    dispatch(flowReset())
  }

  const handleSaveSegment = (name: string) => {
    dispatch(
      segmentNameSubmittedThunk({
        name,
        autoApproveEnabled,
        preference,
        buildSegmentData: (_name, autoApprove) =>
          buildSegmentData(_name, autoApprove),
      })
    )
  }

  const handleChangePrefClicked = () => {
    dispatch(
      activationStarted({
        origin: 'change-preference',
        seedAutoApprove:
          effectiveAutoApprove != null
            ? effectiveAutoApprove
              ? 'activate'
              : 'save-only'
            : null,
        seedRememberChoice: preference?.autoApproveAiSegments != null,
      })
    )
  }

  const handleNameChange = (n: string) => {
    dispatch(nameEdited(n))
  }

  // Activation modal named handlers
  const handleReviewOptionSelected = () => {
    dispatch(activationOptionSelected('save-only'))
  }

  const handleActivateOptionSelected = () => {
    dispatch(activationOptionSelected('activate'))
  }

  const handleActivateOptionKeyDown = (e: KeyboardEvent) => {
    if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) {
      dispatch(
        activationOptionSelected(
          ['ArrowDown', 'ArrowRight'].includes(e.key) ? 'activate' : 'save-only'
        )
      )
    }
  }

  const handleRememberToggled = () => {
    dispatch(rememberChoiceToggled())
  }

  const handleActivationSaveClicked = () => {
    dispatch(
      activationSaveClickedThunk({
        updatePreference,
        currentPreference: preference,
        buildSegmentData: (_name, autoApprove) =>
          buildSegmentData(_name, autoApprove),
      })
    )
  }

  const handleCloseClicked = () => {
    dispatch(activationCancelled())
  }

  const handleCreateCampaignClick = () => {
    if (data.savedSegmentId) {
      onCampaignCreate(data.savedSegmentId)
    }
  }

  const handleViewSegmentClick = () => {
    if (data.savedSegmentId) {
      // The card's title is the clean segment name; the persisted record's is
      // suffixed with a hash and its draft state, so titling the modal from the
      // card avoids both the suffix and a wait on the fetch.
      dispatch(
        openSegmentDetailsModal({
          segmentId: data.savedSegmentId,
          title: data.title,
        })
      )
    }
  }

  const isSegmentSaved =
    data.segmentState === 'requires_approval' || data.segmentState === 'visible'

  return (
    <>
      <div
        className="thanx-ai-segment-visualization"
        role="region"
        aria-label="Segment visualization"
      >
        <div className="thanx-ai-segment-visualization__filters">
          <SegmentFiltersHeader
            estimatedCount={data.estimatedCount}
            onViewClick={handleViewCustomers}
          />

          <SegmentGroupsList groups={groups} />

          <Switch condition={hasRawDsl && aiSegmentDevtoolsEnabled}>
            <SegmentRawDsl rawDsl={data.rawDsl!} />
          </Switch>
        </div>

        <Switch condition={!isSegmentSaved}>
          <SegmentActions
            showCustomizeButton={aiSegmentDetailsEnabled}
            onSaveClick={handleSaveClick}
            onViewSegmentClick={handleViewSegmentClick}
            savedSegmentId={data.savedSegmentId}
            isLatestDraftSegment={isLatestDraftSegment}
            hasAnySavedSegment={hasAnySavedSegment}
          />
        </Switch>
        <Switch condition={isSegmentSaved}>
          <SegmentCampaignActions
            onCreateCampaignClick={handleCreateCampaignClick}
            onViewSegmentClick={handleViewSegmentClick}
            isLoading={isCampaignLoading}
            savedSegmentId={data.savedSegmentId}
            isLatestSavedSegment={isLatestSavedSegment}
          />
        </Switch>
      </div>

      <ThanxAISegmentNameModal
        isOpen={isNameModalOpen}
        onCancel={handleNameModalClose}
        onSubmit={handleSaveSegment}
        initialValue={activationFlow.segmentName}
        showPreferenceBanner={autoApproveEnabled}
        preference={
          effectiveAutoApprove != null
            ? { autoApprove: effectiveAutoApprove }
            : null
        }
        onChangePreferenceClicked={handleChangePrefClicked}
        onNameChange={handleNameChange}
      />

      {isOwnFlow && activationFlow.phase === 'activating' && (
        <SegmentActivationModal
          isOpen={true}
          autoApprove={activationFlow.pendingAutoApprove === 'activate'}
          rememberChoice={activationFlow.rememberChoice}
          isSaving={activationFlow.isSaving}
          error={activationFlow.error}
          onReviewOptionSelected={handleReviewOptionSelected}
          onActivateOptionSelected={handleActivateOptionSelected}
          onActivateOptionKeyDown={handleActivateOptionKeyDown}
          onRememberToggled={handleRememberToggled}
          onSaveClicked={handleActivationSaveClicked}
          onCloseClicked={handleCloseClicked}
        />
      )}
    </>
  )
}

export default ThanxAISegmentVisualization
